import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useRef } from 'react'
import { DialogContent, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { DisbursementMutationDto, DisbursementOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import FileCombobox, { FileComboboxProps } from '../../../../components/Combobox/FileCombobox'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { useFormikContext } from 'formik'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import useRoles, { rolesWriteAllTimes } from '../../../../hooks/useRoles'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import { useFormikFieldStyles } from '../../../../components/Formik/FormikField'
import { IRefObject } from '@fluentui/utilities'
import { ITextField } from '@fluentui/react'
import useCurrency from '../../../../hooks/useCurrency'
import FormikMoneyField from '../../../../components/Formik/FormikMoneyField'
import FormContainer from '../../Form/FormContainer'
import useDerivedFormValues from '../../../hooks/useDerivedFormValues'
import useDisbursementTypes from '../../../../hooks/useDisbursementTypes'
import useVatTypes from '../../../../hooks/useVatTypes'
import DisbursementTypeCombobox, { DisbursementTypeComboboxProps } from '../../../../components/Combobox/DisbursementTypeCombobox'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../../../components/Combobox/i18n/VatTypeCombobox'
import { multiplyRate } from '../../../../helpers/numberHelper'
import { VatType } from '../../../../config/i18nConfig'
import { IFileDto } from '../../../../config/fileConfig'
import useAxios from '../../../../hooks/useAxios'
import FormikDatePickerField from '../../../../components/Formik/FormikDatePickerField'

const useStyles = makeStyles({
    rateRow: {
        display: 'grid',
        // Without this the fields keep Fluent's intrinsic min-width and the dialog scrolls sideways in a task pane.
        '& > *': { minWidth: 0 },
        '& .fui-Field *': { minWidth: 0 },
        gridTemplateColumns: `1fr 1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: `2fr 3fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRowSmall: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
})

// Verschottype is verplicht zodra er types zijn ingericht (HAM-2410).
export const getValidationSchema = (hasDisbursementTypes: boolean, translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        fileId: Yup.string().uuid().label(translate('registration.fileid')).required(),
        userId: Yup.string().uuid().label(translate('registration.userid')).required(),
        type: hasDisbursementTypes
            ? Yup.string().label(translate('disbursement.type')).required()
            : Yup.string().label(translate('disbursement.type')).nullable(),
        description: Yup.string().label(translate('registration.description')).required(),
        rate: Yup.string().label(translate('registration.rate')).required(),
        vatType: Yup.string().label(translate('disbursement.vattype')).required(),
        date: Yup.string().label(translate('registration.date')).required(),
    })
}

export default function DisbursementMutationDialogBodyContent(
    props: HammockFormDialogProps<DisbursementMutationDto, BaseMutableOverviewDialogProps<DisbursementOverviewDto>> & {
        focusTrigger?: number
        updateFocusTrigger?: () => void
    },
): ReactElement {
    const axios = useAxios()
    const roles = useRoles()
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { initialValues, values, setFieldValue } = useFormikContext<DisbursementMutationDto>()
    const { applyDerivedValues, onKeyDownCapture } = useDerivedFormValues<DisbursementMutationDto>()

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<DisbursementOverviewDto>), [props])

    const viewportSmall = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const isInvoiceDraft: boolean = dialogBodyProps.item?.invoice?.status === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])

    const { loaded: disbursementTypesLoaded, disbursementTypes, getDisbursementType } = useDisbursementTypes()
    const { getVatType } = useVatTypes()
    const vatType = useMemo<VatType>(() => getVatType(values.vatType), [values.vatType])

    const fileFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const typeFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = useRef<ITextField>()
    const rateFocusRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!values.fileId) {
            fileFocusRef.current?.focus()
        } else if (disbursementTypes.length > 0 && !values.type) {
            typeFocusRef.current?.focus()
        } else if (!!initialValues.description || !values.description) {
            descriptionFocusRef.current?.focus()
        } else {
            rateFocusRef.current?.focus()
        }
    }, [values.fileId, values.type, props.focusTrigger, disbursementTypesLoaded])

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(disbursementTypes.length > 0, translate))
    }, [disbursementTypes])

    useEffect(() => {
        // Like the old inline row: fall back to the file's VAT type while no type is chosen.
        if (!dialogBodyProps.id && values.fileId) {
            axios.get<IFileDto>(`/file/${values.fileId}`).then(response =>
                applyDerivedValues({
                    ...(values.vatType ? {} : { vatType: response.data.vatType }),
                    ...(values.currency ? {} : { currency: response.data.currency }),
                }),
            )
        }
    }, [values.fileId])

    useEffect(() => {
        // The total including VAT is display-only, like the total column of the old inline list.
        applyDerivedValues({
            rateTotal: multiplyRate((vatType?.percentage ?? 0) / 100 + 1, {
                amount: values.rate,
                currency: values.currency,
            }).amount,
        })
    }, [values.rate, vatType])

    return (
        <DialogContent onKeyDownCapture={onKeyDownCapture}>
            <FormContainer>
                {props.unsavedWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('base.unsaved.dialog')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceDraft && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('disbursement.edit.form.invoice.draft')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceFinal && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('disbursement.edit.form.invoice.final')}</MessageBarBody>
                    </MessageBar>
                )}

                <FormikComboboxField<DisbursementMutationDto, FileComboboxProps>
                    required
                    fieldName={'fileId'}
                    component={FileCombobox}
                    placeholderKey={'registration.fileid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || isInvoiceDraft,
                        disabled: isInvoiceFinal || isInvoiceDraft,
                        componentRef: fileFocusRef,
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <FormikComboboxField<DisbursementMutationDto, DisbursementTypeComboboxProps>
                    required={disbursementTypesLoaded && disbursementTypes?.length > 0}
                    fieldName={'type'}
                    component={DisbursementTypeCombobox}
                    placeholderKey={'disbursement.type'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        componentRef: typeFocusRef,
                        onOptionSelect: (_, data) => {
                            const disbursementType = getDisbursementType(data.selectedOptions?.[0])
                            setFieldValue('type', data.selectedOptions?.[0])
                                .then(() => setFieldValue('vatType', disbursementType?.vatType))
                                .then(() => setFieldValue('description', disbursementType?.description || disbursementType?.name))
                        },
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <FormikTextareaField<DisbursementMutationDto>
                    fieldName="description"
                    componentProps={{
                        required: true,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        ref: descriptionFocusRef,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikComboboxField<DisbursementMutationDto, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        disabled: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <div className={styles.rateRow}>
                    <FormikMoneyField<DisbursementMutationDto>
                        required
                        fieldName={'rate'}
                        placeholderKey={'registration.rate'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            contentBefore: currencySymbol,

                            input: { ref: rateFocusRef },
                        }}
                    />
                    <FormikComboboxField<DisbursementMutationDto, VatTypeComboboxProps>
                        required
                        fieldName={'vatType'}
                        component={VatTypeCombobox}
                        placeholderKey={'base.vattypecombobox.placeholder'}
                        componentProps={{
                            placeholderPrefixKey: '',
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                        }}
                    />
                    <FormikMoneyField<DisbursementMutationDto>
                        required
                        fieldName={'rateTotal'}
                        placeholderKey={'registration.rate'}
                        componentProps={{
                            readOnly: true,
                            disabled: isInvoiceFinal,
                            tabIndex: -1,
                            appearance: 'filled-darker',
                            contentBefore: currencySymbol,
                        }}
                    />
                </div>
                <div className={mergeClasses(styles.dateRow, viewportSmall && styles.dateRowSmall)}>
                    <FormikDatePickerField<DisbursementMutationDto>
                        required
                        fieldName={'date'}
                        placeholderKey={'registration.date'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                        }}
                    />
                </div>
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
